<template>
  <div>
    <div class="item">
      <!-- Отображение системных ответов -->
      <div v-if="systemAnswers.length" class="answers-section">
        <div v-for="(systemItem, index) in systemAnswers" :key="`system-${index}`" class="system-wrapper">
          <h4 class="system-title">{{ formatKey(systemItem.name) || 'Системный ответ' }}</h4>
          <div v-for="(value, key) in sortedKeys(systemItem.result)" :key="key" class="wrapper">
            <template v-if="key !== 'hidden'">
              <RecursiveRenderer
                :data="value"
                :label="formatKey(key)"
                :depth="0"
              />
            </template>
          </div>
        </div>
      </div>

      <!-- Отображение кастомных ответов -->
      <div v-if="customAnswers.length" class="answers-section">
        <div v-for="(customItem, index) in customAnswers" :key="`custom-${index}`" class="system-wrapper">
          <h4 class="system-title">{{ formatKey(customItem.name) || 'Кастомный ответ' }}</h4>
          <template v-if="customItem.result">
            <div v-for="(value, key) in sortedKeys(customItem.result)" :key="key" class="wrapper">
              <template v-if="key !== 'hidden'">
                <RecursiveRenderer
                  :data="value"
                  :label="formatKey(key)"
                  :depth="0"
                />
              </template>
            </div>
          </template>
          <template v-else>
            <div v-for="(value, key) in sortedKeys(customItem)" :key="key" class="wrapper">
              <template v-if="key !== 'name' && key !== 'hidden'">
                <RecursiveRenderer
                  :data="value"
                  :label="formatKey(key)"
                  :depth="0"
                />
              </template>
            </div>
          </template>
        </div>
      </div>

      <div v-if="otherData.length" class="answers-section">
        <div class="system-wrapper">
          <h4 class="system-title">Другие данные</h4>
          <div v-for="(value, key) in sortedKeys(filteredData)" :key="key" class="wrapper">
            <RecursiveRenderer
              :data="value"
              :label="formatKey(key)"
              :depth="0"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "QuestionsCallForm",
  components: {
    RecursiveRenderer: {
      name: 'RecursiveRenderer',
      functional: true,
      props: {
        data: {
          type: [Object, Array, String, Number, Boolean],
          required: true
        },
        label: {
          type: String,
          default: ''
        },
        depth: {
          type: Number,
          default: 0
        }
      },
      render(h, context) {
        const { data, label, depth } = context.props

        const formatKey = (key) => {
          if (!key) return '';
          return String(key)
            .replace(/_/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
        };

        const formatValue = (value) => {
          if (value === null || value === undefined) return 'Не указано';
          if (Array.isArray(value)) return value.join(', ') || 'Не указано';
          if (typeof value === 'object') return 'Объект';
          return value.toString();
        };

        const isObject = (value) => {
          return typeof value === 'object' && value !== null && !Array.isArray(value);
        };

        const isArray = (value) => {
          return Array.isArray(value);
        };

        // Если это объект (не массив)
        if (isObject(data)) {
          const entries = Object.entries(data)
            .filter(([key]) => key !== 'hidden');

          if (entries.length === 0) return null;

          // Определяем заголовок в зависимости от глубины
          let titleElement = null;
          if (label) {
            const titleClass = depth === 0 ? 'legend' :
                              depth === 1 ? 'sub-legend' : 'deep-legend';
            const tag = depth === 0 ? 'h5' :
                       depth === 1 ? 'h6' : 'h7';

            titleElement = h(tag, {
              class: titleClass
            }, formatKey(label));
          }

          return h('div', {
            class: depth === 0 ? 'wrapper' :
                   depth === 1 ? 'nested-wrapper' : 'deep-wrapper'
          }, [
            titleElement,
            ...entries.map(([key, value], index) =>
              h('RecursiveRenderer', {
                key: `entry-${key}-${index}-${depth}`,
                props: {
                  data: value,
                  label: key,
                  depth: depth + 1
                }
              })
            )
          ]);
        }

        // Если это массив - выводим как простое значение (как в оригинале)
        if (isArray(data)) {
          if (data.length === 0) return null;

          // Выводим массив как простое значение, не как список
          const displayValue = formatValue(data);
          if (!displayValue) return null;

          return h('p', {
            class: 'question'
          }, [
            label && depth === 0 && h('span', { class: 'question-label' }, `${formatKey(label)}: `),
            label && depth === 1 && h('span', { class: 'question-label' }, `${formatKey(label)}: `),
            label && depth === 2 && h('span', { class: 'question-label' }, `${formatKey(label)}: `),
            h('span', { class: 'question-value' }, displayValue)
          ]);
        }

        // Простое значение
        const displayValue = formatValue(data);
        if (!displayValue) return null;

        return h('p', {
          class: 'question'
        }, [
          label && depth === 0 && h('span', { class: 'question-label' }, `${formatKey(label)}: `),
          label && depth === 1 && h('span', { class: 'question-label' }, `${formatKey(label)}: `),
          label && depth === 2 && h('span', { class: 'question-label' }, `${formatKey(label)}: `),
          h('span', { class: 'question-value' }, displayValue)
        ]);
      }
    }
  },
  props: {
    answerBody: {
      type: Object,
      required: true,
    },
  },
  computed: {
    systemAnswers() {
      const answers = this.answerBody.data?.answers || this.answerBody.answers;
      return answers?.system || [];
    },

    customAnswers() {
      const answers = this.answerBody.data?.answers || this.answerBody.answers;
      return answers?.custom || [];
    },

    filteredData() {
      const data = this.answerBody.data || this.answerBody;
      const filtered = { ...data };

      if (filtered.answers) {
        delete filtered.answers;
      }

      return filtered;
    },

    otherData() {
      return Object.keys(this.filteredData).length > 0;
    }
  },
  methods: {
    formatKey(key) {
      if (!key) return '';
      return String(key)
        .replace(/_/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },

    formatValue(value) {
      if (value === null || value === undefined) return 'Не указано';
      if (Array.isArray(value)) return value.join(', ') || 'Не указано';
      if (typeof value === 'object') return 'Объект';
      return value.toString();
    },

    isObject(value) {
      return typeof value === 'object' && value !== null;
    },

    sortedKeys(obj) {
      if (!obj || typeof obj !== 'object') return {};

      const simpleKeys = [];
      const objectKeys = [];
      const arrayKeys = [];

      Object.keys(obj).forEach(key => {
        if (key === 'hidden') return;

        const value = obj[key];
        if (Array.isArray(value)) {
          arrayKeys.push(key);
        } else if (this.isObject(value)) {
          objectKeys.push(key);
        } else {
          simpleKeys.push(key);
        }
      });

      const sortedObj = {};
      [...simpleKeys, ...arrayKeys, ...objectKeys].forEach(key => {
        sortedObj[key] = obj[key];
      });

      return sortedObj;
    }
  },
};
</script>

<style lang="scss" scoped>
.item {
  line-height: 1;
  .system-wrapper {
    margin-bottom: 20px;
    border: 1px solid #ddd;
    padding: 10px;
    border-radius: 5px;

    .system-title {
      font-size: 14px;
      font-weight: bold;
      color: #333;
      margin: 0 0 10px 0;
      padding-bottom: 5px;
      border-bottom: 1px solid #eee;
    }

    .wrapper {
      margin-bottom: 10px;
      padding-bottom: 10px;
      border-bottom: 1px solid #eee;

      &:last-child {
        border-bottom: none;
      }

      .legend {
        font-weight: bold;
        color: #333;
        font-size: 12px;
        margin-bottom: 5px;
        text-decoration: underline;
      }

      .sub-legend {
        font-weight: bold;
        color: #555;
        font-size: 11px;
        margin: 0px 0 3px 0;
        padding-left: 10px;
        border-left: 3px solid #3498db;
      }

      .deep-legend {
        font-weight: bold;
        color: #777;
        font-size: 10px;
        margin: 3px 0 2px 15px;
        padding-left: 8px;
        border-left: 2px solid #ddd;
      }

      .nested-wrapper {
        padding-bottom: 5px;
        padding-left: 5px;
        border-left: 1px solid #eee;
      }

      .deep-wrapper {
        margin-left: 10px;
        margin-bottom: 5px;
        padding-left: 8px;
        border-left: 1px dashed #ddd;
      }

      .question {
        color: #555;
        font-size: 10px;
        font-weight: bold;
        margin-bottom: 0px;
        margin-left: 15px;

        .question-label {
          font-weight: bold;
        }

        .question-value {
          font-weight: normal;
          color: #333;
        }
      }
    }
  }
}
</style>